import { API_URL } from "@/config";
import { createClient } from "@/lib/client";
import type {
  ConversationDetail,
  ConversationSummary,
  ParsedAssistant,
  Source,
} from "@/lib/types";

const supabase = createClient();

export async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const token = await getAuthToken();
  if (!token) return [];
  const res = await fetch(`${API_URL}/conversations`, {
    headers: { Authorization: token },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.conversations ?? [];
}

export async function fetchConversation(
  conversationId: string,
): Promise<ConversationDetail | null> {
  const token = await getAuthToken();
  if (!token) return null;
  const res = await fetch(`${API_URL}/conversation/${conversationId}`, {
    headers: { Authorization: token },
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data.conversation ?? null;
}

export function parseAssistant(buffer: string, done = false): ParsedAssistant {
  const convMatch = buffer.match(
    /<CONVERSATION_ID>([^<]+)<\/CONVERSATION_ID>/,
  );
  const conversationId = convMatch?.[1]?.trim();

  const answerMatch = buffer.match(
    /<ANSWER>([\s\S]*?)(?:<\/ANSWER>|$)/,
  );
  let answer = answerMatch?.[1] ?? "";
  // strip trailing partial tags during streaming
  answer = answer.replace(/<\/?[A-Z_]*$/i, "").trim();

  const followUps: string[] = [];
  const fuMatch = buffer.match(
    /<FOLLOW_UPS>([\s\S]*?)(?:<\/FOLLOW_UPS>|$)/,
  );
  if (fuMatch) {
    const re = /<question>\s*([\s\S]*?)\s*<\/question>/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(fuMatch[1])) !== null) {
      followUps.push(m[1].trim());
    }
  }

  let sources: Source[] = [];
  const srcMatch = buffer.match(
    /<SOURCES>\s*([\s\S]*?)\s*<\/SOURCES>/,
  );
  if (srcMatch) {
    try {
      const parsed = JSON.parse(srcMatch[1]);
      if (Array.isArray(parsed)) sources = parsed;
    } catch {}
  }

  return { conversationId, answer, followUps, sources, done };
}

type StreamArgs = {
  endpoint: "/query" | "/query/followup";
  body: Record<string, unknown>;
  onUpdate: (parsed: ParsedAssistant, raw: string) => void;
  signal?: AbortSignal;
};

export async function streamAssistant({
  endpoint,
  body,
  onUpdate,
  signal,
}: StreamArgs): Promise<{ raw: string; parsed: ParsedAssistant }> {
  const token = await getAuthToken();
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(`${API_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Request failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    onUpdate(parseAssistant(buffer, false), buffer);
  }
  buffer += decoder.decode();
  const finalParsed = parseAssistant(buffer, true);
  onUpdate(finalParsed, buffer);
  return { raw: buffer, parsed: finalParsed };
}
